import { useState, useRef, useCallback } from 'react';
import { analyzeIngredientsAPI, getIngredientsAPI, getIngredientsByBarcodeAPI, getIngredientsByImageAPI, trackAnalyticsEventAPI } from '../utils/api';

const cleanOptionalProductLabel = (value) => {
  const label = String(value || '').trim();
  if (!label) return undefined;
  const normalized = label.toLowerCase();
  if (normalized === 'null' || normalized === 'undefined') return undefined;
  return label;
};

export const useVegAIAnalyze = (mode, { foodProfile } = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Server-side error code (e.g. 'BARCODE_NOT_FOUND') so panels can branch on
  // failure type without parsing the user-facing message string.
  const [errorCode, setErrorCode] = useState(null);
  const [errorContext, setErrorContext] = useState(null);
  const [result, setResult] = useState(null);
  const [variants, setVariants] = useState([]);
  const [brands, setBrands] = useState([]);
  const [pendingProduct, _setPendingProduct] = useState(null);
  const pendingProductRef = useRef(null);
  const setPendingProduct = (valOrFn) => {
    _setPendingProduct((prev) => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      pendingProductRef.current = next;
      return next;
    });
  };

  const sessionRef = useRef(0);
  const foodProfileRef = useRef(foodProfile);
  foodProfileRef.current = foodProfile;

  const featureForInput = (input) => {
    if (mode === 'manual') return 'manual';
    if (mode === 'product') return 'product';
    if (mode === 'scanner') {
      if (input && typeof input === 'object' && input.image) return 'image';
      if (input && typeof input === 'object' && input.type === 'scannedIngredients') return 'scanner';
      return 'barcode';
    }
    return 'app';
  };

  const isCancelled = (id) => sessionRef.current !== id;

  // For text-only variant chips (no offCode/fdcId), prepend the original product
  // query and selected brand so the search finds the right product instead of
  // orphaned flavor names like "Shrimp Flavor".
  const buildContextualLookup = (label, ctx) => {
    if (!ctx) return label;
    const parts = [];
    if (ctx.brand) parts.push(ctx.brand);
    if (ctx.productContext) parts.push(ctx.productContext);
    if (parts.length === 0) return label;
    const prefix = parts.join(' ');
    const labelLower = label.toLowerCase();
    const prefixLower = prefix.toLowerCase();
    if (labelLower.includes(prefixLower) || prefixLower.includes(labelLower)) return label;
    return `${prefix} ${label}`;
  };

  const analyzeWithIngredients = async (
    ingredients,
    verifyMatch,
    productName,
    productMatch,
    sources,
    sessionId,
    uiSource,
    resolvedVia,
    sourceEvidence,
    scannedIngredients
  ) => {
    const analysisResult = await analyzeIngredientsAPI(
      ingredients,
      verifyMatch,
      productName,
      productMatch,
      sources,
      uiSource,
      resolvedVia,
      sourceEvidence,
      foodProfileRef.current
    );
    if (resolvedVia) {
      analysisResult.resolvedVia = resolvedVia;
    }
    if (Array.isArray(scannedIngredients) && scannedIngredients.length > 0) {
      analysisResult.scannedIngredients = scannedIngredients;
    }
    if (!isCancelled(sessionId)) {
      setResult(analysisResult);
      return analysisResult;
    }
    return undefined;
  };

  const inFlightRef = useRef(false);

  const analyze = async (input) => {
    if (!input) return undefined;
    if (typeof input === 'string' && !input.trim()) return undefined;
    if (typeof input === 'object' && !input.image && input.type !== 'scannedIngredients') return undefined;

    // Reject overlapping calls explicitly. The previous 500 ms timestamp
    // throttle silently dropped clicks, leaving the UI in a confusing state.
    if (inFlightRef.current) return undefined;
    inFlightRef.current = true;

    const sessionId = ++sessionRef.current;
    const feature = featureForInput(input);
    trackAnalyticsEventAPI({
      eventName: 'analysis_submit',
      feature,
      metadata: { mode },
    });

    setIsLoading(true);
    setError(null);
    setErrorCode(null);
    setErrorContext(null);
    setResult(null);
    setVariants([]);
    setBrands([]);
    setPendingProduct(null);

    let outcome = undefined;

    try {
      if (mode === 'manual') {
        const analysisResult = await analyzeIngredientsAPI(
          input,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          foodProfileRef.current
        );
        if (!isCancelled(sessionId)) {
          setResult(analysisResult);
          outcome = analysisResult;
        }
      } else if (mode === 'scanner') {
        let ingredients, verifyMatch, productMatch, returnedVariants, returnedBrands, sources, resolvedVia, sourceEvidence;
        let scannerImageMode = null;

        if (typeof input === 'object' && input.type === 'scannedIngredients') {
          ingredients = Array.isArray(input.ingredients)
            ? input.ingredients.map((x) => String(x || '').trim()).filter(Boolean)
            : [];
          if (ingredients.length === 0) {
            throw new Error('That history entry does not include readable ingredients.');
          }
          scannerImageMode = input.type;
          verifyMatch = input.verifyMatch;
          productMatch = cleanOptionalProductLabel(input.productMatch);
          sources = Array.isArray(input.sources) ? input.sources : [];
          resolvedVia = input.resolvedVia || null;
          sourceEvidence = input.sourceEvidence || null;
        } else if (typeof input === 'object' && input.type && input.image) {
          if (input.type !== 'ingredients') {
            throw new Error('Please scan a barcode or use an ingredient list photo.');
          }
          scannerImageMode = input.type;
          ({
            ingredients,
            verifyMatch,
            productMatch,
            variants: returnedVariants,
            brands: returnedBrands,
            sources,
            resolvedVia,
            sourceEvidence,
          } = await getIngredientsByImageAPI(input.image, input.type));
        } else {
          ({ ingredients, verifyMatch, productMatch, sources, resolvedVia, sourceEvidence } = await getIngredientsByBarcodeAPI(input));
        }

        if (isCancelled(sessionId)) return undefined;

        // Ingredient-list photos already provide the ingredients. Do not route
        // them through product-match confirmation just because OCR confidence is
        // below the product lookup threshold.
        if (scannerImageMode === 'ingredients' || scannerImageMode === 'scannedIngredients') {
          outcome = await analyzeWithIngredients(
            ingredients,
            verifyMatch,
            undefined,
            undefined,
            sources,
            sessionId,
            'scanner',
            resolvedVia,
            sourceEvidence,
            ingredients
          );
          return outcome;
        }

        // Scanner image mode now only accepts ingredient-list photos. Keep the
        // picker handling for defensive compatibility with older responses.
        if (returnedBrands && returnedBrands.length >= 2) {
          setBrands(returnedBrands);
          setPendingProduct({
            ingredients: ingredients || [],
            verifyMatch,
            productMatch,
            originalInput: productMatch || '',
            sources,
            resolvedVia,
            sourceEvidence,
            allBrands: returnedBrands,
          });
          setIsLoading(false);
          return undefined;
        }
        if (returnedVariants && returnedVariants.length > 1) {
          setVariants(returnedVariants);
          setPendingProduct({
            ingredients: ingredients || [],
            verifyMatch,
            productMatch,
            originalInput: productMatch || '',
            sources,
            resolvedVia,
            sourceEvidence,
          });
          setIsLoading(false);
          return undefined;
        }

        // Low-confidence scanner hits (camera misread, LLM-only barcode
        // fallback, fuzzy photo match) should pause for confirmation the same
        // way the product-name flow does, instead of silently returning a
        // potentially wrong vegan/not-vegan verdict.
        if (typeof verifyMatch === 'number' && verifyMatch < 0.90 && (productMatch || (ingredients && ingredients.length > 0))) {
          setPendingProduct({
            ingredients: ingredients || [],
            verifyMatch,
            productMatch,
            originalInput: productMatch || '',
            sources,
            resolvedVia,
            sourceEvidence,
            needsConfirmation: true,
            uiSource: 'scanner',
          });
          setIsLoading(false);
          return undefined;
        }

        // For scanner-resolved products we don't have a user-typed product
        // label, so productName === productMatch is the best signal we have.
        outcome = await analyzeWithIngredients(
          ingredients,
          verifyMatch,
          productMatch || undefined,
          productMatch || undefined,
          sources,
          sessionId,
          'scanner',
          resolvedVia,
          sourceEvidence
        );
      } else if (mode === 'product') {
        const {
          ingredients,
          verifyMatch,
          productMatch,
          variants: returnedVariants,
          brands: returnedBrands,
          sources,
          resolvedVia,
          sourceEvidence,
        } = await getIngredientsAPI(input);

        if (isCancelled(sessionId)) return undefined;

        // Multi-brand: ask user to pick a brand first; variants come after.
        if (returnedBrands && returnedBrands.length >= 2) {
          setBrands(returnedBrands);
          setPendingProduct({
            ingredients,
            verifyMatch,
            productMatch,
            originalInput: input.trim(),
            sources,
            resolvedVia,
            sourceEvidence,
            allBrands: returnedBrands,
          });
          setIsLoading(false);
          return undefined;
        }

        // Product/version choices should pause before analysis. Picker-only
        // responses intentionally return no ingredients for broad searches
        // such as "ramen" or "Doritos".
        if (returnedVariants && returnedVariants.length > 0 && ((ingredients || []).length === 0 || returnedVariants.length > 1)) {
          setVariants(returnedVariants);
          setPendingProduct({ ingredients, verifyMatch, productMatch, originalInput: input.trim(), sources, resolvedVia, sourceEvidence });
          setIsLoading(false);
          return undefined;
        }

        // High-confidence single match — analyze directly without confirmation.
        if (verifyMatch >= 0.90) {
          outcome = await analyzeWithIngredients(
            ingredients,
            verifyMatch,
            input.trim(),
            productMatch,
            sources,
            sessionId,
            'product',
            resolvedVia,
            sourceEvidence
          );
        } else {
          setPendingProduct({
            ingredients,
            verifyMatch,
            productMatch,
            originalInput: input.trim(),
            sources,
            resolvedVia,
            sourceEvidence,
            needsConfirmation: true,
          });
          setIsLoading(false);
          return undefined;
        }
      } else {
        throw new Error(`Unknown mode: ${mode}`);
      }
    } catch (err) {
      if (!isCancelled(sessionId)) {
        setError(err.message || 'Failed to analyze. Please try again.');
        setErrorCode(err.code || null);
        setErrorContext(err.barcode ? { barcode: err.barcode, reason: err.reason || null, productMatch: err.productMatch || null } : null);
        trackAnalyticsEventAPI({
          eventName: 'analysis_failure',
          feature,
          metadata: { mode, errorCode: err.code || 'unknown' },
        });
      }
    } finally {
      if (!isCancelled(sessionId)) {
        setIsLoading(false);
      }
      inFlightRef.current = false;
    }

    if (outcome) {
      trackAnalyticsEventAPI({
        eventName: 'analysis_success',
        feature,
        metadata: { mode, resultStatus: outcome.status || 'unknown' },
      });
    }

    return outcome;
  };

  // User picked a brand chip → populate the variant chips for that brand (no API call).
  // If the brand has exactly one option, skip the variant picker and analyze directly.
  const selectBrand = (brandObj) => {
    const options = Array.isArray(brandObj?.options) ? brandObj.options : [];
    const brandName = brandObj?.brand || null;
    setBrands([]);
    setPendingProduct((prev) => prev ? { ...prev, selectedBrand: brandName } : prev);
    if (options.length === 1) {
      setVariants([]);
      const ctx = { productContext: pendingProductRef.current?.originalInput, brand: brandName };
      analyzeVariant(options[0], ctx);
    } else {
      setVariants(options);
    }
  };

  const analyzeVariant = async (variant, context) => {
    const ctx = context || {
      productContext: pendingProductRef.current?.originalInput,
      brand: pendingProductRef.current?.selectedBrand,
    };
    const sessionId = ++sessionRef.current;

    setIsLoading(true);
    setError(null);
    setErrorCode(null);
    setErrorContext(null);
    setResult(null);
    setVariants([]);
    setBrands([]);
    setPendingProduct(null);

    try {
      let lookup;
      let displayName;
      if (typeof variant === 'object' && variant !== null) {
        if (variant.offCode != null && String(variant.offCode).trim()) {
          lookup = { offCode: String(variant.offCode).trim() };
          displayName = (variant.label && String(variant.label).trim()) || `Product ${variant.offCode}`;
        } else if (variant.fdcId != null && variant.fdcId !== '') {
          lookup = { fdcId: Number(variant.fdcId) };
          displayName = (variant.label && String(variant.label).trim()) || `Product (USDA)`;
        } else if (variant.label != null && String(variant.label).trim()) {
          const rawLabel = String(variant.label).trim();
          displayName = rawLabel;
          lookup = buildContextualLookup(rawLabel, ctx);
        } else {
          throw new Error('Invalid variant selection.');
        }
      } else {
        displayName = String(variant || '').trim();
        if (!displayName) throw new Error('Invalid variant selection.');
        lookup = buildContextualLookup(displayName, ctx);
      }

      const { ingredients, verifyMatch, productMatch, sources, resolvedVia, sourceEvidence } = await getIngredientsAPI(lookup);
      if (!isCancelled(sessionId)) {
        return await analyzeWithIngredients(
          ingredients,
          verifyMatch,
          displayName,
          productMatch,
          sources,
          sessionId,
          'product',
          resolvedVia,
          sourceEvidence
        );
      }
    } catch (err) {
      if (!isCancelled(sessionId)) {
        setError(err.message || 'Failed to analyze. Please try again.');
        setErrorCode(err.code || null);
        setErrorContext(err.barcode ? { barcode: err.barcode, reason: err.reason || null, productMatch: err.productMatch || null } : null);
      }
    } finally {
      if (!isCancelled(sessionId)) {
        setIsLoading(false);
      }
    }
    return undefined;
  };

  const analyzeDefault = async () => {
    // Read from the ref (not the `pendingProduct` state closure) so a rapid
    // "confirm" click during a concurrent render can't pick up a stale product
    // object and analyze the wrong ingredient list.
    const pending = pendingProductRef.current;
    if (!pending) return;
    const { ingredients, verifyMatch, productMatch, originalInput, sources, resolvedVia, sourceEvidence, uiSource } = pending;
    const sessionId = ++sessionRef.current;

    setIsLoading(true);
    setError(null);
    setErrorCode(null);
    setErrorContext(null);
    setVariants([]);
    setBrands([]);
    setPendingProduct(null);

    try {
      return await analyzeWithIngredients(
        ingredients,
        verifyMatch,
        originalInput,
        productMatch,
        sources,
        sessionId,
        uiSource || 'product',
        resolvedVia,
        sourceEvidence
      );
    } catch (err) {
      if (!isCancelled(sessionId)) {
        setError(err.message || 'Failed to analyze. Please try again.');
        setErrorCode(err.code || null);
        setErrorContext(err.barcode ? { barcode: err.barcode, reason: err.reason || null, productMatch: err.productMatch || null } : null);
      }
    } finally {
      if (!isCancelled(sessionId)) {
        setIsLoading(false);
      }
    }
    return undefined;
  };

  const confirmAndAnalyze = async () => {
    const pending = pendingProductRef.current;
    if (!pending || !pending.needsConfirmation) return undefined;
    return analyzeDefault();
  };

  const reset = useCallback(() => {
    sessionRef.current++;
    setResult(null);
    setError(null);
    setErrorCode(null);
    setErrorContext(null);
    setIsLoading(false);
    setVariants([]);
    setBrands([]);
    setPendingProduct(null);
  }, []);

  /** Step back in product disambiguation without clearing the search field (full reset uses `reset`). */
  const backFromProductSelection = useCallback(() => {
    if (brands.length >= 2) {
      setBrands([]);
      setVariants([]);
      setPendingProduct(null);
      return;
    }
    if (
      variants.length > 0 &&
      pendingProduct?.allBrands &&
      pendingProduct.allBrands.length >= 2
    ) {
      setBrands(pendingProduct.allBrands);
      setVariants([]);
      return;
    }
    if (variants.length > 0) {
      setVariants([]);
      setPendingProduct(null);
    }
  }, [brands, variants, pendingProduct]);

  return {
    analyze,
    analyzeVariant,
    analyzeDefault,
    confirmAndAnalyze,
    selectBrand,
    backFromProductSelection,
    isLoading,
    error,
    errorCode,
    errorContext,
    result,
    variants,
    brands,
    pendingProduct,
    reset,
  };
};
